import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date, money, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Stat, Table } from '../../components/ui';
import { DateInput, Drawer, Field, Select } from '../../components/ui/Form';

interface CashAccount {
  id: string;
  code: string;
  name: string;
  type: string;
  balance: string;
}

interface Closing {
  id: string;
  closing_date: string;
  account_name: string;
  branch_name: string;
  opening_amount: string;
  receipts_amount: string;
  payments_amount: string;
  expected_amount: string;
  counted_amount: string;
  variance_amount: string;
  status: string;
  explanation: string | null;
  counted_by_name: string | null;
  approved_by_name: string | null;
}

/** Indian notes and coins, largest first — the order a drawer is counted in. */
const DENOMINATIONS = [500, 200, 100, 50, 20, 10, 5, 2, 1];

interface CountResult {
  closingId: string;
  expectedAmount: string;
  countedAmount: string;
  varianceAmount: string;
  needsExplanation: boolean;
}

/**
 * The till count.
 *
 * The expected figure is deliberately absent from this screen until the count
 * has been submitted. An operator who is told what to find will find it, and a
 * count that agrees because it was told the answer catches nothing.
 */
export function CashPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const { branches, defaultBranchId } = useBranches();

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<CountResult | null>(null);
  const [counts, setCounts] = useState<Record<number, string>>({});
  const [form, setForm] = useState({ accountId: '', closingDate: today(), branchId: '' });
  const [explanation, setExplanation] = useState('');

  const accounts = useQuery({
    queryKey: ['cashAccounts'],
    queryFn: () => api.get<{ data: CashAccount[] }>('/cash/accounts'),
  });
  const closings = useQuery({
    queryKey: ['cashClosings'],
    queryFn: () => api.get<{ data: Closing[] }>('/cash/closings'),
  });

  const accountList = accounts.data?.data ?? [];
  const cash = accountList.filter((a) => a.type === 'CASH');
  const bank = accountList.filter((a) => a.type === 'BANK');

  /** The count total comes from the notes, so it cannot disagree with them. */
  const countedAmount = useMemo(() => DENOMINATIONS.reduce((a, d) => a + d * Number(counts[d] ?? 0), 0), [counts]);

  const count = useMutation({
    mutationFn: () =>
      api.post<CountResult>('/cash/closings', {
        branchId: form.branchId || defaultBranchId,
        accountId: form.accountId,
        closingDate: form.closingDate,
        countedAmount: countedAmount.toFixed(2),
        denominations: Object.fromEntries(
          DENOMINATIONS.filter((d) => Number(counts[d] ?? 0) > 0).map((d) => [String(d), Number(counts[d])]),
        ),
      }),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
      void client.invalidateQueries({ queryKey: ['cashClosings'] });
    },
    onError: setError,
  });

  const explain = useMutation({
    mutationFn: (id: string) => api.post(`/cash/closings/${id}/explain`, { explanation }),
    onSuccess: () => {
      setOpen(false);
      setResult(null);
      setCounts({});
      setExplanation('');
      void client.invalidateQueries({ queryKey: ['cashClosings'] });
    },
    onError: setError,
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/cash/closings/${id}/approve`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['cashClosings'] }),
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    count.mutate();
  };

  const rows = closings.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Cash and bank"
        subtitle="What is in each drawer, and how the counts have gone."
        actions={
          can('cash.count') ? (
            <button
              className="btn-primary"
              onClick={() => {
                setOpen(true);
                setResult(null);
                setCounts({});
              }}
            >
              Count a till
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {accounts.isLoading ? <Loading /> : null}

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cash.map((a) => (
          <Stat key={a.id} label={a.name} value={money(a.balance)} hint="per the ledger" />
        ))}
        {bank.map((a) => (
          <Stat key={a.id} label={a.name} value={money(a.balance)} hint="per the ledger" />
        ))}
      </div>

      <h2 className="mb-2 text-sm font-semibold">Till counts</h2>
      {rows.length === 0 ? (
        <Empty message="No tills counted yet. A drawer that is never counted is a drawer nobody is watching." />
      ) : (
        <Table
          head={[
            'Date',
            'Till',
            'Branch',
            'Expected',
            'Counted',
            'Difference',
            { label: 'Counted by', align: 'left' },
            'Status',
            '',
          ]}
        >
          {rows.map((c) => {
            const variance = Number(c.variance_amount);
            return (
              <tr key={c.id}>
                <td className="td">{date(c.closing_date)}</td>
                <td className="td">{c.account_name}</td>
                <td className="td">{c.branch_name}</td>
                <td className="td num text-right">{money(c.expected_amount)}</td>
                <td className="td num text-right">{money(c.counted_amount)}</td>
                <td className="td num text-right">
                  {variance === 0 ? (
                    <span className="text-success">exact</span>
                  ) : (
                    <span className={variance < 0 ? 'text-danger' : 'text-gold-700'}>
                      {variance > 0 ? '+' : ''}
                      {money(c.variance_amount)}
                    </span>
                  )}
                </td>
                <td className="td text-xs">{c.counted_by_name ?? '—'}</td>
                <td className="td text-xs">
                  {c.status.toLowerCase()}
                  {c.explanation ? <span className="block text-ink-soft">{c.explanation}</span> : null}
                </td>
                <td className="td">
                  {c.status === 'EXPLAINED' && can('cash.approve') ? (
                    <button className="btn-ghost py-1 text-xs" onClick={() => approve.mutate(c.id)}>
                      Approve
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </Table>
      )}

      {open ? (
        <Drawer
          title="Count the till"
          subtitle="Count the drawer first. What the books expect is shown afterwards."
          onClose={() => {
            setOpen(false);
            setResult(null);
          }}
        >
          <ErrorNotice error={error} />

          {result === null ? (
            <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
              <Field label="Till" required>
                <Select
                  value={form.accountId}
                  onChange={(v) => setForm({ ...form, accountId: v })}
                  options={accountList.map((a) => ({ value: a.id, label: a.name }))}
                />
              </Field>

              <Field label="For the day ending" required>
                <DateInput value={form.closingDate} onChange={(v) => setForm({ ...form, closingDate: v })} />
              </Field>

              {branches.length > 1 ? (
                <Field label="Branch" required>
                  <Select
                    value={form.branchId || (defaultBranchId ?? '')}
                    onChange={(v) => setForm({ ...form, branchId: v })}
                    options={branches.map((b) => ({ value: b.id, label: b.name }))}
                  />
                </Field>
              ) : null}

              <div>
                <p className="label">Notes and coins</p>
                <div className="space-y-1 rounded border border-ink/15 bg-white p-3">
                  {DENOMINATIONS.map((d) => (
                    <div key={d} className="flex items-center gap-3">
                      <span className="num w-14 text-right text-sm">₹{d}</span>
                      <span className="text-ink-soft">×</span>
                      <input
                        className="field num w-20 px-2 py-1 text-sm"
                        inputMode="numeric"
                        value={counts[d] ?? ''}
                        onChange={(e) => setCounts({ ...counts, [d]: e.target.value.replace(/[^\d]/g, '') })}
                      />
                      <span className="num ml-auto text-sm text-ink-soft">
                        {money((d * Number(counts[d] ?? 0)).toFixed(2))}
                      </span>
                    </div>
                  ))}
                  <div className="mt-2 flex items-center justify-between border-t border-ink/10 pt-2">
                    <span className="text-sm font-medium">In the drawer</span>
                    <span className="num text-lg font-semibold">{money(countedAmount.toFixed(2))}</span>
                  </div>
                </div>
              </div>

              <button
                className="btn-primary w-full"
                disabled={!form.accountId || countedAmount <= 0 || count.isPending}
              >
                {count.isPending ? 'Recording…' : 'Submit the count'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="card">
                <div className="flex justify-between text-sm">
                  <span>You counted</span>
                  <span className="num font-medium">{money(result.countedAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>The books expect</span>
                  <span className="num font-medium">{money(result.expectedAmount)}</span>
                </div>
                <div className="mt-2 flex justify-between border-t border-ink/10 pt-2">
                  <span className="font-medium">Difference</span>
                  <span
                    className={`num text-lg font-bold ${
                      Number(result.varianceAmount) === 0
                        ? 'text-success'
                        : Number(result.varianceAmount) < 0
                          ? 'text-danger'
                          : 'text-gold-700'
                    }`}
                  >
                    {Number(result.varianceAmount) === 0 ? 'Exact' : money(result.varianceAmount)}
                  </span>
                </div>
              </div>

              {result.needsExplanation ? (
                <>
                  <Field
                    label="What happened?"
                    required
                    hint="Kept permanently, and a manager has to approve it before it reaches the books."
                  >
                    <textarea
                      className="field h-24"
                      value={explanation}
                      onChange={(e) => setExplanation(e.target.value)}
                      placeholder="Change given from my own pocket, not yet reimbursed"
                    />
                  </Field>
                  <button
                    className="btn-primary w-full"
                    disabled={explanation.trim().length < 3 || explain.isPending}
                    onClick={() => explain.mutate(result.closingId)}
                  >
                    {explain.isPending ? 'Saving…' : 'Submit explanation'}
                  </button>
                </>
              ) : (
                <button
                  className="btn-primary w-full"
                  onClick={() => {
                    setOpen(false);
                    setResult(null);
                    setCounts({});
                  }}
                >
                  Done — the drawer is exact
                </button>
              )}
            </div>
          )}
        </Drawer>
      ) : null}
    </>
  );
}
